import type { AgentState, CallLogEntry, CampaignConfig, Lead, LoginResponse, RoomState, VisualState, WsMessage } from './types';

export interface Call {
  outbound_call_id: string;
  reservation_id: string;
  lead: Lead | null;
  offered_at: number;
  bridged_at: number | null;
  ended_at: number | null;
  customer: { cid_num: string; cid_name: string } | null;
  held: boolean;
  muted: boolean;
  /** On-demand recording running (AE-10987); not rebuilt on resync, so it can read false after a reload. */
  recording?: boolean;
}

export type UiPhase = 'login' | 'idle' | 'incoming' | 'live' | 'hungup' | 'dispo' | 'wrapup';

export interface DesktopState {
  session: { token: string; agent_id: string; tenant: string; campaign_id: string; room: string } | null;
  config: CampaignConfig | null;
  agentState: AgentState;
  pauseCode: string | null;
  phase: UiPhase;
  call: Call | null;
  room: RoomState | null;
  upstream: boolean;
  webphone: 'off' | 'registering' | 'registered' | 'in-room' | 'failed';
  pauseAfterCall: boolean;
  modal: 'none' | 'pause' | 'callback' | 'callbacks' | 'calllog' | 'transfer' | 'manualdial';
  pendingDispo: string | null;
  callLog: CallLogEntry[];
  error: string | null;
  stateSince: number;
}

export const initialState: DesktopState = {
  session: null, config: null, agentState: 'offline', pauseCode: null, phase: 'login', call: null, room: null,
  upstream: false, webphone: 'off', pauseAfterCall: false, modal: 'none', pendingDispo: null, callLog: [], error: null, stateSince: Date.now(),
};

export type Action =
  | { type: 'LOGIN_OK'; body: LoginResponse; now: number }
  | { type: 'LOGOUT' }
  | { type: 'TOKEN'; token: string }
  | { type: 'AGENT_STATE'; state: AgentState; pauseCode?: string | null; now: number }
  | { type: 'RESYNC'; state: AgentState; pauseCode?: string | null; reservation: { reservation_id: string; outbound_call_id: string; lead_id: string; created_at: number; bridged_at?: number; ended_at?: number } | null; lead: Lead | null; now: number }
  | { type: 'WS'; msg: WsMessage; now: number }
  | { type: 'ROOM_ACTION'; held?: boolean; muted?: boolean; recording?: boolean }
  | { type: 'WEBPHONE'; status: DesktopState['webphone'] }
  | { type: 'CUSTOMER_GONE'; now: number }
  | { type: 'OPEN_DISPO' }
  | { type: 'DISPO_DONE'; state: AgentState; pauseCode: string | null; now: number; code: string; wrapupSeconds: number }
  | { type: 'WRAPUP_OVER' }
  | { type: 'MODAL'; modal: DesktopState['modal'] }
  | { type: 'PENDING_DISPO'; code: string | null }
  | { type: 'PAUSE_AFTER'; value: boolean }
  | { type: 'CALLLOG'; calls: CallLogEntry[] }
  | { type: 'ERROR'; message: string | null };

export function reducer(s: DesktopState, a: Action): DesktopState {
  switch (a.type) {
    case 'LOGIN_OK':
      return {
        ...initialState,
        session: { token: a.body.token, agent_id: a.body.agent.agent_id, tenant: a.body.agent.tenant, campaign_id: a.body.agent.campaign_id, room: a.body.agent.room },
        config: a.body.campaign, agentState: a.body.agent.state, phase: 'idle', stateSince: a.now,
      };
    case 'LOGOUT':
      return { ...initialState, stateSince: Date.now() };
    case 'TOKEN':
      // A refreshed bearer (AE-10888) changes nothing else: never a re-login, so a live call survives.
      return s.session ? { ...s, session: { ...s.session, token: a.token } } : s;
    case 'RESYNC': {
      // /api/me after a reconnect (AE-10930): rebuild the call from the reservation agent-state
      // still holds, so a desktop that came back mid-call shows the live call (or the dispo
      // screen), not an idle desk with an agent the pacer thinks is on_call.
      if (!a.reservation) {
        const call = s.call && (a.state === 'wrap' || a.state === 'on_call' || a.state === 'reserved') ? s.call : null;
        const phase: UiPhase = a.state === 'wrap' ? 'dispo' : a.state === 'on_call' || a.state === 'reserved' ? s.phase : s.phase === 'wrapup' ? 'wrapup' : 'idle';
        return { ...s, agentState: a.state, pauseCode: a.pauseCode ?? (a.state === 'paused' ? s.pauseCode : null), call, phase, stateSince: a.now };
      }
      const r = a.reservation;
      const same = s.call && s.call.outbound_call_id === r.outbound_call_id ? s.call : null;
      const call: Call = {
        outbound_call_id: r.outbound_call_id, reservation_id: r.reservation_id, lead: a.lead ?? same?.lead ?? null,
        offered_at: r.created_at, bridged_at: r.bridged_at ?? same?.bridged_at ?? null, ended_at: r.ended_at ?? same?.ended_at ?? null,
        customer: same?.customer ?? null, held: same?.held ?? false, muted: same?.muted ?? false, recording: same?.recording,
      };
      const phase: UiPhase = call.ended_at !== null || a.state === 'wrap' ? 'dispo' : call.bridged_at !== null || a.state === 'on_call' ? 'live' : 'incoming';
      return { ...s, agentState: a.state, pauseCode: a.pauseCode ?? null, call, phase, stateSince: a.now };
    }
    case 'AGENT_STATE': {
      const phase: UiPhase = a.state === 'wrap' ? (s.phase === 'dispo' ? 'dispo' : 'hungup') : a.state === 'on_call' || a.state === 'reserved' ? (s.phase === 'incoming' || s.phase === 'live' ? s.phase : 'live') : s.phase === 'wrapup' ? 'wrapup' : 'idle';
      return { ...s, agentState: a.state, pauseCode: a.pauseCode ?? (a.state === 'paused' ? s.pauseCode : null), phase, stateSince: a.now };
    }
    case 'WS': {
      const m = a.msg;
      if (m.type === 'upstream') return { ...s, upstream: m.connected };
      if (m.type === 'room') {
        const cust = m.room.customers[0] ?? null;
        const call = s.call ? { ...s.call, customer: cust ? { cid_num: cust.cid_num, cid_name: cust.cid_name } : s.call.customer } : s.call;
        return { ...s, room: m.room, call };
      }
      if (m.type === 'call_offered') {
        return {
          ...s, agentState: 'reserved', phase: 'incoming', stateSince: a.now,
          call: { outbound_call_id: m.outbound_call_id, reservation_id: m.reservation_id, lead: m.lead, offered_at: m.occurred_at, bridged_at: null, ended_at: null, customer: null, held: false, muted: false },
        };
      }
      if (m.type === 'call_bridged') {
        const call = s.call && s.call.outbound_call_id === m.outbound_call_id
          ? { ...s.call, bridged_at: m.occurred_at, lead: s.call.lead ?? m.lead }
          : { outbound_call_id: m.outbound_call_id, reservation_id: m.reservation_id, lead: m.lead, offered_at: m.occurred_at, bridged_at: m.occurred_at, ended_at: null, customer: null, held: false, muted: false };
        return { ...s, agentState: 'on_call', phase: 'live', call, stateSince: a.now };
      }
      if (m.type === 'call_handoff') {
        if (m.role === 'to') {
          // The customer is already in this agent's room: show the live call with the lead.
          const call: Call = s.call && s.call.outbound_call_id === m.outbound_call_id
            ? { ...s.call, reservation_id: m.reservation_id, bridged_at: m.occurred_at, ended_at: null, lead: m.lead ?? s.call.lead }
            : { outbound_call_id: m.outbound_call_id, reservation_id: m.reservation_id, lead: m.lead, offered_at: m.occurred_at, bridged_at: m.occurred_at, ended_at: null, customer: null, held: false, muted: false };
          return { ...s, agentState: 'on_call', phase: 'live', call, stateSince: a.now };
        }
        // Handed away: nothing left to disposition here.
        if (s.call && s.call.outbound_call_id !== m.outbound_call_id) return s;
        return { ...s, agentState: m.source_state ?? 'ready', phase: 'idle', call: null, room: null, modal: 'none', pendingDispo: null, stateSince: a.now };
      }
      if (m.type === 'call_ended') {
        // A tab that attaches mid-wrap is replayed only this event (AE-10931), so build the call
        // from it: the dispo screen needs the lead for the contact, script and callback timezone.
        const call = s.call
          ? { ...s.call, ended_at: m.occurred_at, held: false, lead: s.call.lead ?? m.lead }
          : { outbound_call_id: m.outbound_call_id, reservation_id: m.reservation_id, lead: m.lead, offered_at: m.occurred_at, bridged_at: null, ended_at: m.occurred_at, customer: null, held: false, muted: false };
        return { ...s, agentState: 'wrap', phase: 'dispo', call, stateSince: a.now };
      }
      return s;
    }
    case 'ROOM_ACTION':
      return s.call ? { ...s, call: { ...s.call, held: a.held ?? s.call.held, muted: a.muted ?? s.call.muted, recording: a.recording ?? s.call.recording } } : s;
    case 'WEBPHONE':
      return { ...s, webphone: a.status };
    case 'CUSTOMER_GONE':
      return s.phase === 'live' ? { ...s, phase: 'hungup', stateSince: a.now } : s;
    case 'OPEN_DISPO':
      return { ...s, phase: 'dispo' };
    case 'DISPO_DONE': {
      const entry: CallLogEntry | null = s.call ? { outbound_call_id: s.call.outbound_call_id, lead_id: s.call.lead?.lead_id ?? '', phone_number: s.call.lead?.phone_number ?? null, started_at: s.call.offered_at, ended_at: s.call.ended_at ?? a.now, disposition: a.code } : null;
      const wrapup = a.wrapupSeconds > 0 && a.state === 'ready';
      return {
        ...s, agentState: a.state, pauseCode: a.pauseCode, call: null, room: null, pendingDispo: null, modal: 'none',
        phase: wrapup ? 'wrapup' : 'idle', stateSince: a.now, callLog: entry ? [entry, ...s.callLog] : s.callLog,
      };
    }
    case 'WRAPUP_OVER':
      return s.phase === 'wrapup' ? { ...s, phase: 'idle' } : s;
    case 'MODAL':
      return { ...s, modal: a.modal };
    case 'PENDING_DISPO':
      return { ...s, pendingDispo: a.code };
    case 'PAUSE_AFTER':
      return { ...s, pauseAfterCall: a.value };
    case 'CALLLOG':
      return { ...s, callLog: a.calls };
    case 'ERROR':
      return { ...s, error: a.message };
  }
}

/** X5's status-bar colour state from ours. */
export function visualState(s: DesktopState): VisualState {
  switch (s.phase) {
    case 'incoming':
    case 'live':
      return 'live-call';
    case 'hungup':
      return s.agentState === 'on_call' ? 'dead' : 'hung-up';
    case 'dispo':
      return 'hung-up';
    default:
      return s.agentState === 'ready' ? 'locating-contact' : 'paused';
  }
}

/** X5 script merge: --A--field--B-- from the lead's data (plus phone_number / agent name). */
export function mergeScript(html: string, lead: Lead | null, agentName: string): string {
  return html.replace(/--A--([a-zA-Z0-9_]+)--B--/g, (_, field: string) => {
    if (field === 'agent_name') return escapeHtml(agentName);
    if (!lead) return '';
    if (field === 'phone_number') return escapeHtml(lead.phone_number);
    const v = lead.data[field];
    return v === undefined || v === null ? '' : escapeHtml(String(v));
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
}
